import { CheckIcon, CopyIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { shortReference } from './errors';

/**
 * « Référence : 4bf92f35 » + bouton copier : la référence de corrélation d'un échec serveur, que
 * l'utilisateur transmet au support pour retrouver la requête dans les journaux de graal-api.
 * Affiche les 8 premiers caractères, copie la valeur entière. Pas de télémétrie externe : c'est
 * le seul lien entre ce que voit l'utilisateur et ce que le serveur a journalisé.
 */
export function ProblemReference({ traceId, className }: { traceId: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(traceId);
      setCopied(true);
      toast.success(t('patterns.reference.copied'));
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('common.copyFailed'));
    }
  };
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-2xs text-muted-foreground', className)}
      data-testid="problem-reference"
      title={traceId}
    >
      <span>{t('patterns.reference.label')}</span>
      <code className="font-mono text-foreground">{shortReference(traceId)}</code>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={() => void copy()}
        aria-label={t('patterns.reference.copy')}
        data-testid="problem-reference-copy"
      >
        {copied ? <CheckIcon className="text-success-text" /> : <CopyIcon />}
      </Button>
    </span>
  );
}
